import algoliasearch from 'algoliasearch/lite';
import SearchkitClient from '@searchkit/instantsearch-client';
import { simple } from 'instantsearch.js/es/lib/stateMappings';
import { history } from 'instantsearch.js/es/lib/routers';
import { query } from './helpers/mappers';
import WhiteInstantSearch from './WhiteInstantSearch';
import SearchBox from './components/searching/SearchBox';
import Loading from './components/results/Loading';
import NoResults from './components/results/NoResults';
import Hit from './components/results/Hit';
import HitsPerPage from './components/pagination/HitsPerPage';
import Pagination from './components/pagination/Pagination';
import SortBy from './components/sorting/SortBy';
import Stats from './components/metadata/Stats';
import RefinementList from './components/refinements/RefinementList';
import RefinementListPanel from './components/refinements/RefinementListPanel';
import ToggleRefinement from './components/refinements/ToggleRefinement';
import ToggleRefinementPanel from './components/refinements/ToggleRefinementPanel';
import Menu from './components/refinements/Menu';
import MenuPanel from './components/refinements/MenuPanel';
import MenuRadio from './components/refinements/MenuRadio';
import MenuRadioPanel from './components/refinements/MenuRadioPanel';
import MenuSelect from './components/refinements/MenuSelect';
import MenuSelectPanel from './components/refinements/MenuSelectPanel';
import RadixUIRangeSlider from './components/refinements/RadixUIRangeSlider';
import RadixUIPanelRangeSlider from './components/refinements/RadixUIPanelRangeSlider';
import HierarchicalMenu from './components/refinements/HierarchicalMenu';
import HierarchicalMenuPanel from './components/refinements/HierarchicalMenuPanel';
import ClearRefinements from './components/refinements/ClearRefinements';
import CurrentRefinements from './components/refinements/CurrentRefinements';
export default class WhiteInstantSearchBuilder {
    constructor() {
        this.identity = 'white-instant-search-root';
        this.searchClient = null;
        this.searchClientConfig = {};
        this.hitsPerPage = 20;
        this.facetFilters = [];
        this.filters = '';
        this.additionalData = {};
        this.routing = true;
        this.routingSettings = null;
        this.scrolling = null;
        this.callbacks = {
            beforeFirstRender: [],
            afterFirstRender: [],
            afterRender: [],
            onStateChange: [],
        };
        this.searchBoxComponent = {};
        this.autocompleteComponent = {};
        this.hitComponent = {};
        this.hitsPerPageComponent = {};
        this.paginationComponent = {};
        this.sortByComponent = {};
        this.statsComponent = {};
        this.refinementsComponent = {};
        this.clearRefinementsComponent = {};
        this.currentRefinementsComponent = {};
        this.customContainers = [];
    }
    withIdentity(identity) {
        this.identity = identity;
        return this;
    }
    withAlgoliaSearch(indexName, appId, apiKey) {
        this.searchClientConfig['indexName'] = indexName;
        this.searchClientConfig['appId'] = appId;
        this.searchClientConfig['apiKey'] = apiKey;
        this.searchClientConfig['type'] = 'algolia';
        // @ts-ignore
        this.searchClient = algoliasearch(appId, apiKey);
        return this;
    }
    withSearchkit(indexName, url, jwt, refreshIframeUrl) {
        this.searchClientConfig['indexName'] = indexName;
        this.searchClientConfig['url'] = url;
        this.searchClientConfig['jwt'] = jwt;
        this.searchClientConfig['refreshIframeUrl'] = refreshIframeUrl;
        this.searchClientConfig['type'] = 'searchkit';
        const config = {
            url,
            headers: {
                authorization: `Bearer ${jwt}`,
            },
        };
        const searchKitClient = Object.assign(SearchkitClient(config), {
            transporter: {
                headers: {
                    'x-algolia-application-id': 'NULL',
                    'x-algolia-api-key': 'NULL',
                },
            },
        });
        // @ts-ignore
        this.searchClient = jwt ? searchKitClient : null;
        return this;
    }
    setHitsPerPage(hitsPerPage) {
        this.hitsPerPage = hitsPerPage;
        return this;
    }
    setScrolling(settings) {
        this.scrolling = settings;
        return this;
    }
    whereFacet(facetFilter) {
        if (this.facetFilters) {
            return this.andWhereFacet(facetFilter);
        }
        this.facetFilters = [`${facetFilter}`];
        return this;
    }
    andWhereFacet(facetFilter) {
        if (!this.facetFilters) {
            return this.whereFacet(facetFilter);
        }
        this.facetFilters.push(facetFilter);
        return this;
    }
    where(filter) {
        if (this.filters) {
            return this.andWhere(filter);
        }
        this.filters = `(${filter})`;
        return this;
    }
    andWhere(filter) {
        if (!this.filters) {
            return this.where(filter);
        }
        this.filters = `${this.filters} AND (${filter})`;
        return this;
    }
    orWhere(filter) {
        if (!this.filters) {
            return this.where(filter);
        }
        this.filters = `${this.filters} OR (${filter})`;
        return this;
    }
    whereQuery(queryString, mapping = {}) {
        const filters = query.queryStringToFilters(queryString, mapping);
        Object.keys(filters).forEach((attribute) => {
            const values = filters[attribute];
            if (values && values.length > 0) {
                const facets = values.map((value) => (`${attribute}:${value}`)).join(' OR ');
                this.where(facets);
            }
        });
        return this;
    }
    addAdditionalData(data) {
        this.additionalData = Object.assign(Object.assign({}, this.additionalData), data);
        return this;
    }
    setRouting(stateMapping, router) {
        this.routing = {
            stateMapping: stateMapping || simple(),
            router: router ? history(router) : history({ cleanUrlOnDispose: false }),
        };
        this.routingSettings = null;
        return this;
    }
    withoutRouting() {
        this.routing = false;
        this.routingSettings = null;
        return this;
    }
    withDefaultRouting() {
        this.routing = true;
        this.routingSettings = null;
        return this;
    }
    withWhiteInstantSearchRouting(settings) {
        this.routing = settings;
        this.routing.siteUrl = (this.routing.siteUrl || '');
        if (this.routing.siteUrl.endsWith('/')) {
            this.routing.siteUrl = this.routing.siteUrl.slice(0, -1);
        }
        this.routing.basePath = (this.routing.basePath || '').replace(/^\s*\/*\s*|\s*\/*\s*$/gm, '');
        this.routing.addTrailingSlashesToUrls = !!this.routing.addTrailingSlashesToUrls;
        this.routing.pathAttribute = this.routing.pathAttribute || '';
        this.routing.addToPath = !!this.routing.addToPath;
        this.routing.ignoreAttributes = this.routing.ignoreAttributes || [];
        this.routing.attributeMapping = this.routing.attributeMapping || {};
        if (!this.routing.basePath && this.routing.baseUrl) {
            const baseUrl = new URL(this.routing.baseUrl);
            this.routing.basePath = baseUrl.pathname.replace(/^\s*\/*\s*|\s*\/*\s*$/gm, '');
        }
        this.routingSettings = this.routing;
        return this;
    }
    beforeFirstRender(callback) {
        this.callbacks.beforeFirstRender.push(callback);
        return this;
    }
    afterFirstRender(callback) {
        this.callbacks.afterFirstRender.push(callback);
        return this;
    }
    afterRender(callback) {
        this.callbacks.afterRender.push(callback);
        return this;
    }
    onStateChange(callback) {
        this.callbacks.onStateChange.push(callback);
        return this;
    }
    addSearchingComponent(searchBox, selector = null) {
        const key = selector || '#white-instant-search-search-box';
        searchBox.forEach((p) => {
            var _a;
            const defaultSettings = {
                classNames: {
                    form: 'input-group',
                    input: 'form-control',
                    submit: 'btn btn-outline-primary',
                    reset: 'btn btn-outline-danger',
                    loadingIndicator: 'btn btn-secondary disabled',
                },
            };
            const settings = Object.assign(defaultSettings, (p.settings || {}));
            const component = p.component || SearchBox;
            if (!this.searchBoxComponent[key]) {
                this.searchBoxComponent[key] = [];
            }
            (_a = this.searchBoxComponent[key]) === null || _a === void 0 ? void 0 : _a.push({ settings, component });
        });
        return this;
    }
    addDefaultSearchingComponent() {
        return this.addSearchingComponent([{}]);
    }
    addAutocompleteSearchingComponent(autocomplete, selector = null) {
        const key = selector || '#white-instant-search-search-box';
        autocomplete.forEach((p) => {
            var _a;
            const defaultSettings = {
                useRecentSearches: true,
                openOnFocus: true,
                closeOnBlur: false,
                detachedMediaQuery: 'none',
            };
            const settings = Object.assign(defaultSettings, (p.settings || {}));
            if (!this.autocompleteComponent[key]) {
                this.autocompleteComponent[key] = [];
            }
            (_a = this.autocompleteComponent[key]) === null || _a === void 0 ? void 0 : _a.push({ settings });
        });
        return this;
    }
    addHitComponent(hit, selector = null) {
        const key = selector || '#white-instant-search-hits';
        hit.forEach((h) => {
            var _a;
            const settings = h.settings || {};
            const component = h.component || Hit;
            const loadingComponent = h.loadingComponent || Loading;
            const noResultsComponent = h.noResultsComponent || NoResults;
            if (!this.hitComponent[key]) {
                this.hitComponent[key] = [];
            }
            (_a = this.hitComponent[key]) === null || _a === void 0 ? void 0 : _a.push({
                settings,
                component,
                loadingComponent,
                noResultsComponent,
            });
        });
        return this;
    }
    addHitsPerPageComponent(hitsPerPage, selector = null) {
        const key = selector || '#white-instant-search-hits-per-page';
        hitsPerPage.forEach((s) => {
            var _a;
            const defaultSettings = {
                classNames: {
                    control: () => 'form-select hits-per-page',
                    menu: () => 'form-select-list',
                },
            };
            const settings = Object.assign(defaultSettings, s.settings);
            const component = s.component || HitsPerPage;
            if (!this.hitsPerPageComponent[key]) {
                this.hitsPerPageComponent[key] = [];
            }
            (_a = this.hitsPerPageComponent[key]) === null || _a === void 0 ? void 0 : _a.push({ settings, component });
        });
        return this;
    }
    addPaginationComponent(pagination, selector = null) {
        const key = selector || '#white-instant-search-pagination';
        pagination.forEach((p) => {
            var _a;
            const defaultSettings = {
                classNames: {
                    list: 'pagination',
                    item: 'page-item',
                    selectedItem: 'active',
                    disabledItem: 'disabled',
                    link: 'page-link',
                },
                enableCanonicalUpdates: true,
            };
            const settings = Object.assign(defaultSettings, (p.settings || {}));
            const component = p.component || Pagination;
            if (!this.paginationComponent[key]) {
                this.paginationComponent[key] = [];
            }
            (_a = this.paginationComponent[key]) === null || _a === void 0 ? void 0 : _a.push({ settings, component });
        });
        return this;
    }
    addDefaultPaginationComponent() {
        return this.addPaginationComponent([{}]);
    }
    addSortByComponent(sortBy, selector = null) {
        const key = selector || '#white-instant-search-sort-by';
        sortBy.forEach((s) => {
            var _a;
            const defaultSettings = {
                classNames: {
                    control: () => 'form-select sort-by',
                    menu: () => 'form-select-list',
                },
            };
            const settings = Object.assign(defaultSettings, s.settings);
            const component = s.component || SortBy;
            if (!this.sortByComponent[key]) {
                this.sortByComponent[key] = [];
            }
            (_a = this.sortByComponent[key]) === null || _a === void 0 ? void 0 : _a.push({ settings, component });
        });
        return this;
    }
    addStatsComponent(stats, selector = null) {
        const key = selector || '#white-instant-search-stats';
        stats.forEach((s) => {
            var _a;
            const settings = s.settings || {};
            const component = s.component || Stats;
            if (!this.statsComponent[key]) {
                this.statsComponent[key] = [];
            }
            (_a = this.statsComponent[key]) === null || _a === void 0 ? void 0 : _a.push({ settings, component });
        });
        return this;
    }
    addDefaultStatsComponent() {
        return this.addStatsComponent([{}]);
    }
    addRefinements(refinements, type, selector = null) {
        const key = selector || '#white-instant-search-refinements';
        refinements.forEach((r) => {
            var _a;
            const { settings } = r;
            let { component } = r;
            if (!component) {
                if (type === 'refinement-list') {
                    component = RefinementList;
                }
                if (type === 'toggle-refinement') {
                    component = ToggleRefinement;
                }
                if (type === 'menu') {
                    component = Menu;
                }
                if (type === 'hierarchical-menu') {
                    component = HierarchicalMenu;
                }
                if (type === 'range-slider') {
                    component = RadixUIRangeSlider;
                }
            }
            if (component) {
                if (!this.refinementsComponent[key]) {
                    this.refinementsComponent[key] = [];
                }
                (_a = this.refinementsComponent[key]) === null || _a === void 0 ? void 0 : _a.push({ settings, component, type });
            }
        });
        return this;
    }
    addRefinementList(refinementList, selector = null) {
        return this.addRefinements(refinementList, 'refinement-list', selector);
    }
    addRefinementListPanel(refinementList, selector = null) {
        const refinementListPanel = refinementList.map((refinement) => (Object.assign(Object.assign({}, refinement), { component: refinement.component || RefinementListPanel })));
        return this.addRefinementList(refinementListPanel, selector);
    }
    addHiddenRefinementList(refinementList, selector = null) {
        const hidden = refinementList.map((r) => Object.assign(r, { component: () => null }));
        return this.addRefinementList(hidden, selector);
    }
    addToggleRefinement(toggleRefinement, selector = null) {
        return this.addRefinements(toggleRefinement, 'toggle-refinement', selector);
    }
    addToggleRefinementPanel(toggleRefinement, selector = null) {
        const toggleRefinementPanel = toggleRefinement.map((refinement) => (Object.assign(Object.assign({}, refinement), { component: refinement.component || ToggleRefinementPanel })));
        return this.addToggleRefinement(toggleRefinementPanel, selector);
    }
    addHiddenToggleRefinement(toggleRefinement, selector = null) {
        const hidden = toggleRefinement.map((t) => Object.assign(t, { component: () => null }));
        return this.addToggleRefinement(hidden, selector);
    }
    addMenu(menu, selector = null) {
        return this.addRefinements(menu, 'menu', selector);
    }
    addMenuPanel(menu, selector = null) {
        const menuPanel = menu.map((m) => (Object.assign(Object.assign({}, m), { component: m.component || MenuPanel })));
        return this.addMenu(menuPanel, selector);
    }
    addMenuRadio(menu, selector = null) {
        const menuRadio = menu.map((m) => (Object.assign(Object.assign({}, m), { component: m.component || MenuRadio })));
        return this.addMenu(menuRadio, selector);
    }
    addMenuRadioPanel(menu, selector = null) {
        const menuRadioPanel = menu.map((m) => (Object.assign(Object.assign({}, m), { component: m.component || MenuRadioPanel })));
        return this.addMenu(menuRadioPanel, selector);
    }
    addMenuSelect(menu, selector = null) {
        const defaultSettings = {
            classNames: {
                control: () => 'form-select',
                menu: () => 'form-select-list',
            },
        };
        const menuSelect = menu.map((m) => (Object.assign(Object.assign({}, m), { settings: Object.assign(defaultSettings, m.settings), component: m.component || MenuSelect })));
        return this.addMenu(menuSelect, selector);
    }
    addMenuSelectPanel(menu, selector = null) {
        const menuSelectPanel = menu.map((m) => (Object.assign(Object.assign({}, m), { component: m.component || MenuSelectPanel })));
        return this.addMenu(menuSelectPanel, selector);
    }
    addHiddenMenu(menu, selector = null) {
        const hidden = menu.map((m) => Object.assign(m, { component: () => null }));
        return this.addMenu(hidden, selector);
    }
    addHierarchicalMenu(hierarchicalMenu, selector = null) {
        return this.addRefinements(hierarchicalMenu, 'hierarchical-menu', selector);
    }
    addHierarchicalMenuPanel(hierarchicalMenu, selector = null) {
        const hierarchicalMenuPanel = hierarchicalMenu.map((m) => (Object.assign(Object.assign({}, m), { component: m.component || HierarchicalMenuPanel })));
        return this.addHierarchicalMenu(hierarchicalMenuPanel, selector);
    }
    addHiddenHierarchicalMenu(hierarchicalMenu, selector = null) {
        const hidden = hierarchicalMenu.map((h) => Object.assign(h, { component: () => null }));
        return this.addHierarchicalMenu(hidden, selector);
    }
    addRangeSlider(rangeSlider, selector = null) {
        return this.addRefinements(rangeSlider, 'range-slider', selector);
    }
    addRangeSliderPanel(rangeSlider, selector = null) {
        const rangeSliderPanel = rangeSlider.map((r) => (Object.assign(Object.assign({}, r), { component: r.component || RadixUIPanelRangeSlider })));
        return this.addRangeSlider(rangeSliderPanel, selector);
    }
    addHiddenRangeSlider(rangeSlider, selector = null) {
        const hidden = rangeSlider.map((r) => Object.assign(r, { component: () => null }));
        return this.addRangeSlider(hidden, selector);
    }
    addClearRefinementsComponent(clearRefinements, selector = null) {
        const key = selector || '#white-instant-search-clear-refinements';
        clearRefinements.forEach((c) => {
            var _a;
            const settings = c.settings || {};
            const component = c.component || ClearRefinements;
            if (!this.clearRefinementsComponent[key]) {
                this.clearRefinementsComponent[key] = [];
            }
            (_a = this.clearRefinementsComponent[key]) === null || _a === void 0 ? void 0 : _a.push({ settings, component });
        });
        return this;
    }
    addDefaultClearRefinementsComponent() {
        return this.addClearRefinementsComponent([{}]);
    }
    addCurrentRefinementsComponent(currentRefinements, selector = null) {
        const key = selector || '#white-instant-search-current-refinements';
        currentRefinements.forEach((c) => {
            var _a;
            const settings = c.settings || {};
            const component = c.component || CurrentRefinements;
            if (!this.currentRefinementsComponent[key]) {
                this.currentRefinementsComponent[key] = [];
            }
            (_a = this.currentRefinementsComponent[key]) === null || _a === void 0 ? void 0 : _a.push({ settings, component });
        });
        return this;
    }
    addDefaultCurrentRefinementsComponent() {
        return this.addCurrentRefinementsComponent([{}]);
    }
    registerContainer(container, selector) {
        this.customContainers.push({ container, selector });
        return this;
    }
    build() {
        return new WhiteInstantSearch(this.identity, this.searchClientConfig, this.searchClient, this.hitsPerPage, this.facetFilters, this.filters, this.additionalData, this.calculateRouting(), this.routingSettings, this.scrolling, this.callbacks, this.searchBoxComponent, this.autocompleteComponent, this.hitComponent, this.hitsPerPageComponent, this.paginationComponent, this.sortByComponent, this.statsComponent, this.refinementsComponent, this.clearRefinementsComponent, this.currentRefinementsComponent, this.customContainers);
    }
    calculateRouting() {
        if (typeof this.routing === 'boolean') {
            return this.routing;
        }
        if ('stateMapping' in this.routing && 'router' in this.routing) {
            return this.routing;
        }
        const { indexName } = this.searchClientConfig;
        const self = this;
        const { siteUrl, basePath, addTrailingSlashesToUrls, pathAttribute, addToPath, mapping, attributeMapping, ignoreAttributes, windowTitle, writeDelay, } = this.routing;
        let reverseMapping;
        if (mapping) {
            reverseMapping = {};
            Object.keys(mapping).forEach((key) => {
                const values = mapping[key];
                const newValues = {};
                Object.keys(values).forEach((k) => {
                    newValues[values[k]] = k;
                });
                (reverseMapping || {})[key] = newValues;
            });
        }
        return {
            stateMapping: {
                stateToRoute(uiState) {
                    const indexUiState = uiState[indexName];
                    const result = {};
                    Object.keys(self.refinementsComponent).forEach((selector) => {
                        const refinements = self.refinementsComponent[selector];
                        refinements === null || refinements === void 0 ? void 0 : refinements.forEach((refinement) => {
                            let attribute;
                            let value;
                            let separator = '';
                            if (refinement.type === 'refinement-list') {
                                const settings = refinement.settings;
                                attribute = settings.attribute;
                                value = (indexUiState === null || indexUiState === void 0 ? void 0 : indexUiState.refinementList)
                                    ? indexUiState === null || indexUiState === void 0 ? void 0 : indexUiState.refinementList[attribute]
                                    : undefined;
                            }
                            if (refinement.type === 'toggle-refinement') {
                                const settings = refinement.settings;
                                attribute = settings.attribute;
                                value = (indexUiState === null || indexUiState === void 0 ? void 0 : indexUiState.toggle)
                                    ? indexUiState === null || indexUiState === void 0 ? void 0 : indexUiState.toggle[attribute]
                                    : undefined;
                            }
                            if (refinement.type === 'menu') {
                                const settings = refinement.settings;
                                attribute = settings.attribute;
                                value = (indexUiState === null || indexUiState === void 0 ? void 0 : indexUiState.menu)
                                    ? indexUiState === null || indexUiState === void 0 ? void 0 : indexUiState.menu[attribute]
                                    : undefined;
                            }
                            if (refinement.type === 'range-slider') {
                                const settings = refinement.settings;
                                attribute = settings.attribute;
                                value = (indexUiState === null || indexUiState === void 0 ? void 0 : indexUiState.range)
                                    ? indexUiState === null || indexUiState === void 0 ? void 0 : indexUiState.range[attribute]
                                    : undefined;
                            }
                            if (refinement.type === 'hierarchical-menu') {
                                const settings = refinement.settings;
                                [attribute] = settings.attributes;
                                if (attribute) {
                                    value = (indexUiState === null || indexUiState === void 0 ? void 0 : indexUiState.hierarchicalMenu)
                                        ? indexUiState === null || indexUiState === void 0 ? void 0 : indexUiState.hierarchicalMenu[attribute]
                                        : undefined;
                                    separator = settings.separator || '>';
                                }
                            }
                            if (attribute && !(ignoreAttributes === null || ignoreAttributes === void 0 ? void 0 : ignoreAttributes.includes(attribute))) {
                                if (value && mapping && mapping[attribute]) {
                                    if (Array.isArray(value) && separator) {
                                        const newValue = mapping[attribute][value.join(separator)];
                                        value = newValue ? newValue.split(separator) : value;
                                    }
                                    else if (Array.isArray(value)) {
                                        value = value.map((v) => mapping[attribute][v] || v);
                                    }
                                    else {
                                        value = mapping[attribute][value] || value;
                                    }
                                }
                                result[attribute] = value;
                            }
                        });
                    });
                    result['query'] = indexUiState === null || indexUiState === void 0 ? void 0 : indexUiState.query;
                    result['page'] = indexUiState === null || indexUiState === void 0 ? void 0 : indexUiState.page;
                    result['sortBy'] = indexUiState === null || indexUiState === void 0 ? void 0 : indexUiState.sortBy;
                    if (result['sortBy']) {
                        result['sortBy'] = result['sortBy'].replace(indexName, '');
                    }
                    return result;
                },
                routeToState(routeState) {
                    const result = {
                        hierarchicalMenu: {},
                        menu: {},
                        refinementList: {},
                        toggle: {},
                        range: {},
                        query: routeState['query']
                            && Array.isArray(routeState['query'])
                            && routeState['query'].length > 0 ? routeState['query'][0] : undefined,
                        page: routeState['page']
                            && Array.isArray(routeState['page'])
                            && routeState['page'].length > 0 ? routeState['page'][0] : undefined,
                        sortBy: routeState['sortBy']
                            && Array.isArray(routeState['sortBy'])
                            && routeState['sortBy'].length > 0 ? routeState['sortBy'][0] : undefined,
                    };
                    let hasHierarchicalMenu = false;
                    let hasMenu = false;
                    let hasRefinementList = false;
                    let hasToggle = false;
                    let hasRange = false;
                    const hasQuery = Object.keys(self.searchBoxComponent).length > 0
                        || Object.keys(self.autocompleteComponent).length > 0;
                    const hasPagination = Object.keys(self.paginationComponent).length > 0;
                    const hasSortBy = Object.keys(self.sortByComponent).length > 0;
                    Object.keys(self.refinementsComponent).forEach((selector) => {
                        const refinements = self.refinementsComponent[selector];
                        refinements === null || refinements === void 0 ? void 0 : refinements.forEach((refinement) => {
                            let attribute;
                            let type;
                            let separator = '';
                            if (refinement.type === 'refinement-list') {
                                const settings = refinement.settings;
                                attribute = settings.attribute;
                                type = 'refinementList';
                                hasRefinementList = true;
                            }
                            if (refinement.type === 'toggle-refinement') {
                                const settings = refinement.settings;
                                attribute = settings.attribute;
                                type = 'toggle';
                                hasToggle = true;
                            }
                            if (refinement.type === 'menu') {
                                const settings = refinement.settings;
                                attribute = settings.attribute;
                                type = 'menu';
                                hasMenu = true;
                            }
                            if (refinement.type === 'range-slider') {
                                const settings = refinement.settings;
                                attribute = settings.attribute;
                                type = 'range';
                                hasRange = true;
                            }
                            if (refinement.type === 'hierarchical-menu') {
                                const settings = refinement.settings;
                                [attribute] = settings.attributes;
                                type = 'hierarchicalMenu';
                                hasHierarchicalMenu = true;
                                separator = settings.separator || '>';
                            }
                            if (type && attribute && !(ignoreAttributes === null || ignoreAttributes === void 0 ? void 0 : ignoreAttributes.includes(attribute))) {
                                let value = routeState[attribute];
                                if (value && reverseMapping && reverseMapping[attribute]) {
                                    if (Array.isArray(value) && separator) {
                                        const key = attribute;
                                        const newValue = (reverseMapping || {})[key][value.join(separator)];
                                        value = newValue ? newValue.split(separator) : value;
                                    }
                                    else if (Array.isArray(value)) {
                                        value = (value.map((v) => (reverseMapping || {})[attribute][v] || v));
                                    }
                                    else {
                                        value = reverseMapping[attribute][value] || value;
                                    }
                                }
                                result[type][attribute] = value;
                            }
                        });
                    });
                    if (!hasHierarchicalMenu) {
                        delete result['hierarchicalMenu'];
                    }
                    if (!hasMenu) {
                        delete result['menu'];
                    }
                    if (!hasRefinementList) {
                        delete result['refinementList'];
                    }
                    if (!hasToggle) {
                        delete result['toggle'];
                    }
                    if (!hasRange) {
                        delete result['range'];
                    }
                    if (!hasQuery) {
                        delete result['query'];
                    }
                    if (!hasPagination) {
                        delete result['page'];
                    }
                    if (!hasSortBy) {
                        delete result['sortBy'];
                    }
                    if (result['sortBy']) {
                        result['sortBy'] = `${indexName}${result['sortBy']}`;
                    }
                    return {
                        [indexName]: result,
                    };
                },
            },
            router: history({
                cleanUrlOnDispose: false,
                windowTitle,
                createURL({ qsModule, location, routeState }) {
                    const { origin, hash } = location;
                    const queryParameters = {};
                    Object.keys(routeState).forEach((key) => {
                        const value = routeState[key];
                        if (value) {
                            queryParameters[key] = value;
                        }
                    });
                    let slug = '';
                    if (pathAttribute && queryParameters[pathAttribute]) {
                        slug = Array.isArray(queryParameters[pathAttribute])
                            ? queryParameters[pathAttribute].join('/')
                            : queryParameters[pathAttribute];
                        slug = addToPath && basePath ? `/${basePath}/${slug}` : `/${slug}`;
                        delete queryParameters[pathAttribute];
                    }
                    const queryString = qsModule.stringify(queryParameters, {
                        addQueryPrefix: true,
                        arrayFormat: 'repeat',
                    });
                    if (slug) {
                        return `${(siteUrl || origin)}${slug}${addTrailingSlashesToUrls ? '/' : ''}${queryString}${hash}`;
                    }
                    if (basePath) {
                        return `${(siteUrl || origin)}/${basePath}${addTrailingSlashesToUrls ? '/' : ''}${queryString}${hash}`;
                    }
                    return `${(siteUrl || origin)}${addTrailingSlashesToUrls ? '/' : ''}${queryString}${hash}`;
                },
                parseURL({ location }) {
                    var _a;
                    const { pathname, search } = location;
                    const decodedBasePath = query.decodeURIComponent((basePath || ''));
                    const decodedPathname = query.decodeURIComponent(pathname);
                    const values = query.queryStringToFilters(search.slice(1), attributeMapping);
                    const result = {};
                    const regex = new RegExp(`${decodedBasePath}/(.*?)/?$`);
                    const regexResult = decodedPathname.match(regex);
                    const slug = regexResult ? ((_a = regexResult[1]) === null || _a === void 0 ? void 0 : _a.split('/')) : undefined;
                    const hasSlug = slug && slug.length > 0 && slug[0];
                    Object.keys(self.refinementsComponent).forEach((selector) => {
                        const refinements = self.refinementsComponent[selector];
                        refinements === null || refinements === void 0 ? void 0 : refinements.forEach((refinement) => {
                            if (refinement.type === 'refinement-list') {
                                const settings = refinement.settings;
                                let value = settings.attribute === pathAttribute && hasSlug
                                    ? slug
                                    : values[settings.attribute];
                                value = value && value.length > 0 ? value : undefined;
                                result[settings.attribute] = value;
                            }
                            if (refinement.type === 'toggle-refinement') {
                                const settings = refinement.settings;
                                let value = settings.attribute === pathAttribute && hasSlug
                                    ? slug
                                    : values[settings.attribute];
                                value = value && value.length > 0 ? value[0] : undefined;
                                result[settings.attribute] = value;
                            }
                            if (refinement.type === 'menu') {
                                const settings = refinement.settings;
                                let value = settings.attribute === pathAttribute && hasSlug
                                    ? [slug.join('/')]
                                    : values[settings.attribute];
                                value = value && value.length > 0 ? value[0] : undefined;
                                result[settings.attribute] = value;
                            }
                            if (refinement.type === 'range-slider') {
                                const settings = refinement.settings;
                                let value = settings.attribute === pathAttribute && hasSlug
                                    ? slug
                                    : values[settings.attribute];
                                value = value && value.length > 0 ? value[0] : undefined;
                                result[settings.attribute] = value;
                            }
                            if (refinement.type === 'hierarchical-menu') {
                                const settings = refinement.settings;
                                const attribute = settings.attributes[0];
                                if (attribute) {
                                    let value = attribute === pathAttribute && hasSlug
                                        ? slug
                                        : values[attribute];
                                    value = value && value.length > 0 ? value : undefined;
                                    result[attribute] = value;
                                }
                            }
                        });
                    });
                    result['query'] = values['query'];
                    result['page'] = values['page'];
                    result['sortBy'] = values['sortBy'];
                    return result;
                },
                writeDelay,
            }),
        };
    }
}
